<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-yellow" sprite="group-2" />
          Users
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-yellow" sprite="group-2" />
            Users
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-button @click="showModal('Create')">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          stripe
          highlight-hover-row
          show-overflow="tooltip"
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <!-- FORM COMPONENT -->
    <user-form
      :visible="modalVisible"
      :context="modalContext"
      :recordId="modalModelId"
      :timezoneOptions="timezoneOptions"
      :partnerOptions="partnerOptions"
      @modalClosed="handleModalClose"
    >
    </user-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { globalEventBus } from "@/globalEventBus";
import userForm from "./components/form";
import feathersClient from "../../../feathers/feathers-client";
import debounce from "lodash/debounce";
import { ExportToCsv } from "export-to-csv";
import { phoneNumberFormatter } from "@/globals/globals";

export default {
  name: "UsersTable",
  components: { userForm },
  data() {
    return {
      eventChannel: null,
      tableData: [],
      allUsers: [],
      tableLoading: false,
      search: "",
      visibleActionsId: "",
      modalModelId: "",
      modalContext: "",
      modalVisible: false,
      total: 0,
      timezoneOptions: [],
      partnerOptions: [],
      tableColumns: [
        {
          field: "avatar",
          sortable: false,
          showOverflow: false,
          width: 75,
          slots: {
            default: ({ row }) => {
              return [
                <div class="avatar-group">
                  {row.avatarPath && (
                    <div class="contact-avatar">
                      <img
                        src={row.avatarPath}
                        alt={row.name}
                        title={row.name}
                      />
                    </div>
                  )}
                  {!row.avatarPath && (
                    <div class="contact-avatar">
                      {row.email.substring(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>,
              ];
            },
          },
        },
        {
          field: "email",
          title: "Email",
          sortable: true,
          minWidth: 200,
          showOverflow: true,
        },
        {
          field: "userRole",
          title: "Role",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [<span>{this.roleTitle(row.userRole)}</span>];
            },
          },
        },
        {
          field: "extNum",
          title: "Extension",
          sortable: true,
          minWidth: 75,
          showOverflow: false,
        },
        {
          field: "phoneNumbers",
          title: "Direct Dials",
          sortable: true,
          minWidth: 150,
          showOverflow: "tooltip",
          slots: {
            default: ({ row }) => {
              return [
                <span>
                  {row.phoneNumbers
                    .map((item) => this.numberFormatter(item))
                    .join(", ")}
                </span>,
              ];
            },
          },
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.showModal("Edit", row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteRecord(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters("users", { userProfile: "get" }),
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
  },
  watch: {
    user() {
      this.queryUsers();
      if (!this.eventChannel) {
        this.joinSocketChannel();
      }
    },
    search: debounce(function () {
      this.searchUsers();
    }, 500),
    selectedTenant() {
      this.queryUsers();
    },
  },
  methods: {
    ...mapActions("users", {
      removeUser: "remove",
    }),

    searchUsers() {
      if (this.search.length) {
        this.tableData = this.allUsers.filter(
          (data) =>
            data.email.toLowerCase().includes(this.search.toLowerCase()) ||
            (data.extNum
              ? data.extNum.toLowerCase().includes(this.search.toLowerCase())
              : "")
        );
      } else {
        this.tableData = [...this.allUsers];
      }
      this.total = this.tableData.length;
    },

    roleTitle(val) {
      if (+val === 1) return "Platform Admin";
      if (+val === 3) return "Account Admin";
      if (+val === 5) return "User";
      if (+val === 7) return "Queue Manager";
      if (+val === 9) return "Reports Admin";
      if (+val === 11) return "Partner Admin";
      if (+val === 12) return "Pharmacy Owner";
      if (+val === 13) return "Pharmacy Admin";
      if (+val === 14) return "Pharmacy User";
      return "No Role";
    },

    queryUsers() {
      this.tableLoading = true;
      this.tableData = [];
      feathersClient
        .service("users")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            userRole: { $ne: 1 },
            $sort: {
              email: 1,
            },
          },
        })
        .then((res) => {
          const arr = [
            ...res.filter((item) => item.tenantId === this.selectedTenant.id),
          ];
          this.tableData = [...arr];
          this.allUsers = [...arr];
          this.total = res.length;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    showModal(context, editId = null) {
      this.modalVisible = true;
      this.modalContext = context;
      this.modalModelId = editId;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.showModal("Edit", data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },
    deleteRecord(id) {
      this.$confirm("Permanently delete this user?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        type: "warning",
      })
        .then(() => {
          this.tableLoading = true;
          this.removeUser(id)
            .then((res) => {
              this.tableLoading = false;

              this.$notify({
                title: "Success",
                message: "User " + res.email + " has been removed!",
                type: "success",
              });
            })
            .catch((error) => {
              this.tableLoading = false;
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error",
              });
            });
        })
        .catch(() => {
          this.tableLoading = false;

          this.$message({
            type: "info",
            message: "Delete Canceled",
          });
        });
    },

    removeListeners() {
      const userService = feathersClient.service("users");
      userService.removeListener("created");
      userService.removeListener("patched");
      userService.removeListener("removed");
      globalEventBus.$off("appReconnected");
    },

    handleModalClose() {
      this.modalVisible = false;
      this.modalContext = "";
    },

    queryTimezones() {
      feathersClient
        .service("timezones")
        .find({
          query: {
            $sort: { name: 1 },
          },
        })
        .then((res) => (this.timezoneOptions = res));
    },

    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins/users",
      };
      channelService.create(data).then((res) => {
        this.eventChannel = `${data.roomType}`;
      });
    },

    queryPartnerOptions() {
      const authRoles = [1];
      if (authRoles.includes(+this.user.userRole)) {
        feathersClient
          .service("partners")
          .find()
          .then((res) => {
            this.partnerOptions = res;
          });
      }
    },

    numberFormatter(number) {
      return phoneNumberFormatter(number);
    },

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          email: item.email,
          userRole: this.roleTitle(item.userRole),
          phoneNumbers: item.phoneNumbers
            .map((item) => this.numberFormatter(item))
            .join(", "),
          extNum: item.extNum ? item.extNum : "",
        }));

        const options = {
          filename: "users",
          showLabels: true,
          headers: ["Email", "User Role", "Direct Dials", "Extension"],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },
  },
  mounted() {
    if (this.user) {
      this.queryUsers();
      this.queryTimezones();
      this.queryPartnerOptions();
      this.joinSocketChannel();
    }
  },
  created() {
    //rejoin channel on reconnect
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //some special constraints on users force us to write our own logic for real time events.
    feathersClient.service("users").on("created", (item) => {
      //check tenants before adding to store
      const { userRole, tenantIds } = this.user;
      let allowed = 1;

      if (+item.userRole === 1) {
        allowed = 0;
      }

      if (+userRole === 5 || +userRole === 7 || +userRole === 13 || +userRole === 14) {
        allowed = 0;
      }

      if (+userRole === 3 || +userRole === 12) {
        if (
          +item.userRole === 5 ||
          +item.userRole === 7 ||
          +item.userRole === 9 ||
          +item.userRole === 13 ||
          +item.userRole === 14
        ) {
          if (
            !tenantIds.includes(+item.tenantId) ||
            +item.tenantId !== this.selectedTenant.id
          ) {
            allowed = 0;
          }
        }
        if (+item.userRole === 3 || +item.userRole === 12) {
          for (let i = 0; i < item.tenantIds.length; i++) {
            if (!tenantIds.includes(+item.tenantIds[i])) {
              allowed = 0;
            }
            if (!allowed) {
              break;
            }
          }
        }
        if (+item.userRole === 11) {
          allowed = 0;
        }
      }

      if (+userRole === 11) {
        if (
          +item.userRole === 5 ||
          +item.userRole === 7 ||
          +item.userRole === 9 ||
          +item.userRole === 13 ||
          +item.userRole === 14
        ) {
          if (+item.tenantId !== this.selectedTenant.id) {
            allowed = 0;
          }
        }
        if (+item.userRole === 3 || +item.userRole === 12) {
          if (!item.tenantIds.includes(this.selectedTenant.id)) {
            allowed = 0;
          }
        }
        if (+item.userRole === 11 && item.partnerId !== this.user.partnerId) {
          allowed = 0;
        }
      }

      if (+userRole === 1) {
        if (
          +item.userRole === 5 ||
          item.userRole === 7 ||
          +item.userRole === 9 ||
          +item.userRole === 13 ||
          +item.userRole === 14
        ) {
          if (+item.tenantId !== +this.selectedTenant.id) {
            allowed = 0;
          }
        }
        if (+item.userRole === 3 || +item.userRole === 12) {
          if (!item.tenantIds.includes(+this.selectedTenant.id)) {
            allowed = 0;
          }
        }
        if (+item.userRole === 11 && item.tenantId !== this.selectedTenant.id) {
          allowed = 0;
        }
      }
      if (allowed) {
        this.total++;
        this.tableData.push(item);
        this.allUsers.push(item);
      }
    });
    feathersClient.service("users").on("patched", (data) => {
      //check tenants again before adding to store
      const { userRole } = this.$store.state.auth.user;
      let allowed = 1;

      if (+data.userRole === 1) {
        allowed = 0;
      }

      if (+userRole === 3 || +userRole === 12) {
        const { tenantIds } = this.$store.state.auth.user;
        if (
          +data.userRole === 5 ||
          +data.userRole === 7 ||
          +data.userRole === 9 ||
          +data.userRole === 13 ||
          +data.userRole === 14
        ) {
          if (
            !tenantIds.includes(+data.tenantId) ||
            +data.tenantId !== this.selectedTenant.id
          ) {
            allowed = 0;
          }
        }

        if (+data.userRole === 3 || +data.userRole === 12) {
          for (let i = 0; i < data.tenantIds.length; i++) {
            if (!tenantIds.includes(data.tenantIds[i])) {
              allowed = 0;
            }
            if (!allowed) {
              const updateIndex = this.tableData.findIndex(
                (item) => item.id === data.id
              );
              const updateAllIndex = this.allUsers.findIndex(
                (item) => item.id === data.id
              );
              if (updateIndex >= 0) {
                this.tableData.splice(updateIndex, 1);
              }
              if (updateAllIndex >= 0) {
                this.allUsers.splice(updateAllIndex, 1);
              }
              break;
            }
          }
        }

        if (+data.userRole === 11) {
          allowed = 0;
        }
      }

      if (+userRole === 11) {
        if (
          +data.userRole === 5 ||
          +data.userRole === 7 ||
          +data.userRole === 9 ||
          +data.userRole === 13 ||
          +data.userRole === 14
        ) {
          if (+data.tenantId !== this.selectedTenant.id) {
            allowed = 0;
          }
        }
        if (+data.userRole === 3 || +data.userRole === 12) {
          if (!data.tenantIds.includes(this.selectedTenant.id)) {
            allowed = 0;
          }
        }
        if (+data.userRole === 11 && data.partnerId !== this.user.partnerId) {
          allowed = 0;
        }
        if (!allowed) {
          const updateIndex = this.tableData.findIndex(
            (item) => item.id === data.id
          );
          const updateAllIndex = this.allUsers.findIndex(
            (item) => item.id === data.id
          );
          if (updateIndex >= 0) {
            this.tableData.splice(updateIndex, 1);
          }
          if (updateAllIndex >= 0) {
            this.allUsers.splice(updateAllIndex, 1);
          }
        }
      }

      if (+userRole === 1) {
        if (
          +data.userRole === 5 ||
          data.userRole === 7 ||
          +data.userRole === 9 ||
          +data.userRole === 13 ||
          +data.userRole === 14
        ) {
          if (+data.tenantId !== +this.selectedTenant.id) {
            allowed = 0;
          }
        }
        if (+data.userRole === 3 || +data.userRole === 12) {
          if (!data.tenantIds.includes(+this.selectedTenant.id)) {
            allowed = 0;
          }
        }
        if (+data.userRole === 11 && data.tenantId !== this.selectedTenant.id) {
          allowed = 0;
        }
      }

      if (!allowed) {
        const updateIndex = this.tableData.findIndex(
          (item) => item.id === data.id
        );
        const updateAllIndex = this.allUsers.findIndex(
          (item) => item.id === data.id
        );
        if (updateIndex >= 0) {
          this.tableData.splice(updateIndex, 1);
        }
        if (updateAllIndex >= 0) {
          this.allUsers.splice(updateAllIndex, 1);
        }
      }

      if (allowed) {
        //if it is updating an already allowed user
        const updateIndex = this.tableData.findIndex(
          (item) => item.id === data.id
        );
        const updateAllIndex = this.allUsers.findIndex(
          (item) => item.id === data.id
        );
        if (updateIndex >= 0) {
          this.tableData.splice(updateIndex, 1, data);
        }
        if (updateAllIndex >= 0) {
          this.allUsers.splice(updateAllIndex, 1, data);
        }

        //if not found in list but now allowed push it in.
        if (!(updateIndex >= 0 && updateAllIndex >= 0)) {
          this.total++;
          this.tableData.push(data);
          this.allUsers.push(data);
        }
      }
    });
    feathersClient.service("users").on("removed", (data) => {
      this.total--;
      const updateIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const updateAllIndex = this.allUsers.findIndex(
        (item) => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1);
      }
      if (updateAllIndex >= 0) {
        this.allUsers.splice(updateAllIndex, 1);
      }
    });
  },
  beforeDestroy() {
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.eventChannel } });
    this.removeListeners();
  },
};
</script>
